import { TestIcon } from "@mesketch/svg";
import { useCallback, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { ApiError, api, type Intro, type OverviewSession } from "../data/api.js";
import { device, type SessionRecord } from "../data/device.js";
import { useIdentity } from "../data/identity.js";
import { standingOf } from "../data/standing.js";
import { toneOf } from "../components/content.js";
import { ErrorBox, NotFound, useTitle } from "../components/Layout.js";
import { T, TextsProvider } from "../components/texts.js";
import { Tone } from "../components/tone.js";
import { progressOf } from "./HomePage.js";

// Úvod testu (REQ-018): co test měří, z čeho vychází, kolik má kol a minut, že je zdarma a bez
// registrace a že se neptáme na pohlaví. „Začít“ založí sezení (REQ-019). Hotový test (zařízení
// i identita) nabídne hlavně návrat k výsledku, nový průchod až po potvrzení (REQ-041).

export function IntroPage() {
  const intl = useIntl();
  const identity = useIdentity();
  const lang = intl.locale;
  const { slug = "" } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [intro, setIntro] = useState<Intro | null>(null);
  const [status, setStatus] = useState<"loading" | "ok" | "missing" | "error">("loading");
  const [local, setLocal] = useState<readonly SessionRecord[]>([]);
  const [remote, setRemote] = useState<readonly OverviewSession[]>([]);
  // „Udělat test znovu“ ze Skicy otevře rovnou potvrzení.
  const [again, setAgain] = useState(() => (location.state as { again?: boolean } | null)?.again === true);
  const [starting, setStarting] = useState(false);
  useTitle(intro?.texts["methodology.name"]?.value);

  const load = useCallback(() => {
    setStatus("loading");
    api.intro(slug, lang).then(
      (r) => {
        setIntro(r);
        setStatus("ok");
      },
      (e: unknown) => setStatus(e instanceof ApiError && e.status === 404 ? "missing" : "error"),
    );
    void device.sessions().then(setLocal, () => setLocal([]));
    if (identity.kind === "in")
      void api.identityOverview().then(
        (o) => setRemote(o.sessions),
        () => setRemote([]),
      );
  }, [slug, lang, identity.kind]);

  useEffect(load, [load]);

  async function start() {
    if (intro === null) return;
    setStarting(true);
    try {
      const s = await api.createSession(slug, lang);
      await device.saveSession({
        id: s.session_id,
        token: s.token,
        slug,
        locale: s.locale,
        origin: "user",
        createdAt: new Date().toISOString(),
        answered: 0,
        total: intro.items,
        // Na přihlášeném zařízení se sezení připojí rovnou (REQ-032).
        saved: identity.kind === "in",
      });
      // Test z pozvánky do páru (REQ-034): zařízení si k sezení zapamatuje pozvánku, souhlas
      // přijde až na Skice po testu.
      const invite = await device.pendingInvite();
      if (invite !== undefined && invite.slug === slug) {
        await device.setSessionInvite(s.session_id, invite);
        await device.clearPendingInvite();
      }
      navigate(`/${lang}/s/${s.session_id}`);
    } catch (e) {
      // Test se mezitím přepnul na placený (CR-019 §4): úvod se načte znovu a řekne to.
      if (e instanceof ApiError && e.code === "methodology_paid") load();
      else setStatus(e instanceof ApiError && e.status === 404 ? "missing" : "error");
    } finally {
      setStarting(false);
    }
  }

  /** Pokračování sezení identity z jiného zařízení: nový token pro toto zařízení (REQ-032). */
  async function resumeIdentity(sessionId: string) {
    const d = await api.identityDevice(sessionId);
    await device.saveSession({ id: d.session_id, token: d.token, slug: d.slug, locale: d.locale, origin: "user", createdAt: new Date().toISOString(), answered: 0, total: 0 });
    navigate(`/${lang}/s/${d.session_id}`);
  }

  if (status === "missing") return <NotFound />;
  if (status === "error") return <ErrorBox onRetry={load} />;
  if (intro === null) return null;
  const mine = progressOf(slug, local).session;
  const { done, open } = standingOf(slug, lang, local, remote);

  return (
    <TextsProvider texts={intro.texts} locale={lang} preview={false}>
      <Tone slug={slug}>
        <article className="panel stack" data-testid="intro">
          <div className="intro__head">
            <TestIcon tone={toneOf(slug)} variant={done !== null ? "color" : "sketch"} progress={mine === null || mine.total === 0 ? 0 : mine.answered / mine.total} size={56} />
            <h1>
              <T k="methodology.name" />
            </h1>
          </div>
          <p>
            <T k="methodology.claim" />
          </p>
          <ul className="facts">
            <li>
              <FormattedMessage id="intro.rounds" values={{ rounds: intro.rounds }} />
            </li>
            <li>
              <FormattedMessage id="intro.items" values={{ items: intro.items }} />
            </li>
            <li>
              <T k="methodology.duration" />
            </li>
          </ul>
          {intro.entry === "free" ? (
            <p>
              <FormattedMessage id="intro.free" />
            </p>
          ) : null}
          <p data-testid="intro-pause">
            <FormattedMessage id={intro.pause_email === true ? "intro.pause" : "intro.pauseLocal"} />
          </p>
          <p className="muted small">
            <FormattedMessage id={intro.respondentFields?.includes("sex") === true ? "intro.sexAsked" : "intro.sex"} />
          </p>
          <div>
            <h3>
              <FormattedMessage id="intro.basedOn" />
            </h3>
            <p className="muted small">{intro.citation}</p>
          </div>
          {done !== null && open === null ? (
            <p data-testid="intro-done">
              {done.finishedAt === null ? <FormattedMessage id="intro.doneUndated" /> : <FormattedMessage id="intro.doneOn" values={{ date: intl.formatDate(done.finishedAt, { dateStyle: "medium" }) }} />}
            </p>
          ) : null}
          {open !== null ? (
            open.onDevice ? (
              <Link className="button" to={`/${lang}/s/${open.sessionId}`} data-testid="intro-continue">
                <FormattedMessage id="intro.continue" />
              </Link>
            ) : (
              <button type="button" className="button" onClick={() => void resumeIdentity(open.sessionId)} data-testid="intro-continue">
                <FormattedMessage id="intro.continue" />
              </button>
            )
          ) : null}
          {done !== null ? (
            <Link className={open !== null ? "button button--quiet" : "button"} to={done.href} data-testid="intro-result">
              <FormattedMessage id="intro.result" />
            </Link>
          ) : null}
          {!intro.startable ? (
            <p className="notice notice--quiet" data-testid="intro-paid-soon">
              <FormattedMessage id="intro.paidSoon" />
            </p>
          ) : done === null ? (
            <button type="button" className={`button${open !== null ? " button--quiet" : ""}`} onClick={() => void start()} disabled={starting} data-testid="intro-start">
              <FormattedMessage id={open !== null ? "intro.restart" : "intro.start"} />
            </button>
          ) : again ? (
            // Nový průchod u hotového testu až po potvrzení (REQ-041).
            <div className="confirm stack" role="group" data-testid="intro-again-confirm">
              <p className="small">
                <FormattedMessage id="intro.again.text" values={{ duration: <T k="methodology.duration" />, signed: identity.kind === "in" ? "yes" : "no" }} />
              </p>
              <div className="confirm__actions">
                <button type="button" className="button button--quiet" onClick={() => void start()} disabled={starting} data-testid="intro-again-yes">
                  <FormattedMessage id="intro.again.yes" />
                </button>
                <Link className="link-button" to={done.href} data-testid="intro-again-back">
                  <FormattedMessage id="intro.again.back" />
                </Link>
              </div>
            </div>
          ) : (
            <button type="button" className="link-button" onClick={() => setAgain(true)} data-testid="intro-again">
              <FormattedMessage id="intro.again" />
            </button>
          )}
        </article>
      </Tone>
    </TextsProvider>
  );
}
